<template>
	<div class="dynamic-field">
	<!-- Campo booleano -->
		<v-select
			v-if="type === 'boolean'"
			v-model="localValue"
			:items="[
				{ text: 'Vero', value: true },
				{ text: 'Falso', value: false }
			]"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			:menu-props="{'offset-y': true}"
			@change="onChange"
		></v-select>

		<!-- Campo selezione multipla -->
		<v-autocomplete
			v-else-if="multiple"
			v-model="localValue"
			:items="options"
			:label="label + ' Multiplo'"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			multiple
			small-chips
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			:loading="loading"
			:menu-props="{'offset-y': true}"
			@change="onChange"
		></v-autocomplete>

		<!-- Campi con opzioni (select) -->
		<v-autocomplete
			v-else-if="['belongsTo', 'generalTypes', 'morphTo', 'customBelongsTo', 'select', 'enum', 'set'].includes(type)"
			v-model="localValue"
			:items="options"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			:loading="loading"
			:menu-props="{'offset-y': true}"
			@change="onChange"
		></v-autocomplete>

		<!-- Campo data -->
		<gmh-date-picker
			v-else-if="type === 'date'"
			v-model="localValue"
			@change="onChange()"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			prepend-icon="mdi-calendar"
			:append-icon="null"
			:clearable="false"
		></gmh-date-picker>

		<!-- Campo datetime -->
		<!-- NB: resta un selettore di sola data (valore YYYY-MM-DD) come da sempre.
			 Passare a type="datetime" cambierebbe il formato salvato nelle automazioni. -->
		<gmh-date-picker
			v-else-if="type === 'datetime'"
			v-model="localValue"
			@change="onChange()"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			prepend-icon="mdi-calendar-clock"
			:append-icon="null"
			:clearable="false"
		></gmh-date-picker>

		<!-- Campo numerico -->
		<v-text-field
			v-else-if="type === 'number'"
			v-model.number="localValue"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			type="number"
			@input="onChange"
		></v-text-field>

		<!-- Campo email -->
		<v-text-field
			v-else-if="type === 'email'"
			v-model="localValue"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="emailRules"
			:outlined="outlined"
			:dense="dense"
			type="email"
			@input="onChange"
		></v-text-field>

		<!-- Campo tel -->
		<v-text-field
			v-else-if="type === 'tel'"
			v-model="localValue"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			type="tel"
			@input="onChange"
		></v-text-field>

		<!-- Editor di testo avanzato -->
		<v-textarea
			v-else-if="type === 'text-editor'"
			v-model="localValue"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:rows="rows || 4"
			@input="onChange"
		></v-textarea>

		<!-- Campo predefinito (testo) -->
		<v-text-field
			v-else
			v-model="localValue"
			:label="label"
			:hint="hint"
			:persistent-hint="!!hint"
			:disabled="disabled"
			:required="required"
			:rules="rules"
			:outlined="outlined"
			:dense="dense"
			@input="onChange"
		></v-text-field>
  	</div>
</template>

<script>
export default {
  	name: 'DynamicField',
	props: {
		// Proprietà principali
		value: {
			type: [String, Number, Boolean, Array, Object],
			default: null
		},
		type: {
			type: String,
			default: 'text'
		},
		options: {
			type: Array,
			default: () => []
		},
		multiple: {
			type: Boolean,
			default: false
		},
		
		// Proprietà di visualizzazione
		label: {
			type: String,
			default: ''
		},
		hint: {
			type: String,
			default: ''
		},
		outlined: {
			type: Boolean,
			default: true
		},
		dense: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		
		// Proprietà per validation
		required: {
			type: Boolean,
			default: false
		},
		rules: {
			type: Array,
			default: () => []
		},
		
		// Proprietà specifiche
		rows: {
			type: Number,
			default: 4
		}
	},
  
	data() {
		return {
			innerValue: this.value
		}
	},
  
	computed: {
		localValue: {
			get() {
				return this.innerValue
			},
			set(value) {
				this.innerValue = value
			}
		},
		
		emailRules() {
			const baseRules = [...this.rules]
			const emailRule = v => !v || /.+@.+\..+/.test(v) || 'E-mail deve essere valida'
			baseRules.push(emailRule)
			return baseRules
		},
		
		// date e datetime sono resi da gmh-date-picker, che formatta da se'
		displayValue() {
			return this.localValue
		}
	},
  
	watch: {
		value: {
			handler(newVal) {
				this.innerValue = newVal
			},
		immediate: true
		}
	},
  
	methods: {
		onChange() {
			this.$emit('input', this.localValue)
			this.$emit('change', this.localValue)
		}
	}
}
</script>

<style scoped>
	.dynamic-field {
		width: 100%;
	}
</style>
